import { motion, AnimatePresence } from 'framer-motion';

interface SpotlightOverlayProps {
  rect: { top: number; left: number; width: number; height: number } | null;
}

const PADDING = 8;
const BORDER_RADIUS = 12;
const GLOW_COLOR = 'rgba(62, 168, 255, 0.3)';

const springTransition = {
  type: 'spring' as const,
  damping: 30,
  stiffness: 300,
};

export default function SpotlightOverlay({ rect }: SpotlightOverlayProps) {
  const padded = rect
    ? {
        x: rect.left - PADDING,
        y: rect.top - PADDING,
        width: rect.width + PADDING * 2,
        height: rect.height + PADDING * 2,
      }
    : null;

  return (
    <div
      className="fixed inset-0 z-[2000]"
      style={{ pointerEvents: 'none' }}
    >
      {/* SVG overlay: purely visual, no pointer events */}
      <svg
        width="100%"
        height="100%"
        className="absolute inset-0"
        style={{ pointerEvents: 'none' }}
      >
        <defs>
          {/* Mask: white = opaque overlay, black = transparent cutout */}
          <mask id="spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            <AnimatePresence>
              {padded && (
                <motion.rect
                  key="cutout"
                  initial={{
                    x: padded.x,
                    y: padded.y,
                    width: padded.width,
                    height: padded.height,
                  }}
                  animate={{
                    x: padded.x,
                    y: padded.y,
                    width: padded.width,
                    height: padded.height,
                  }}
                  exit={{ opacity: 0 }}
                  transition={springTransition}
                  rx={BORDER_RADIUS}
                  ry={BORDER_RADIUS}
                  fill="black"
                />
              )}
            </AnimatePresence>
          </mask>

          {/* Glow filter */}
          <filter id="spotlight-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="8" />
          </filter>
        </defs>

        {/* Dark overlay with masked cutout */}
        <rect
          width="100%"
          height="100%"
          fill="black"
          opacity={0.75}
          mask="url(#spotlight-mask)"
        />

        {/* Glow effect around cutout */}
        <AnimatePresence>
          {padded && (
            <motion.rect
              key="glow"
              initial={{
                x: padded.x - 4,
                y: padded.y - 4,
                width: padded.width + 8,
                height: padded.height + 8,
                opacity: 0,
              }}
              animate={{
                x: padded.x - 4,
                y: padded.y - 4,
                width: padded.width + 8,
                height: padded.height + 8,
                opacity: 1,
              }}
              exit={{ opacity: 0 }}
              transition={springTransition}
              rx={BORDER_RADIUS + 2}
              ry={BORDER_RADIUS + 2}
              fill="none"
              stroke={GLOW_COLOR}
              strokeWidth={6}
              filter="url(#spotlight-glow)"
            />
          )}
        </AnimatePresence>

        {/* Pulsing ring around cutout */}
        <AnimatePresence>
          {padded && (
            <motion.rect
              key="pulse-ring"
              initial={{
                x: padded.x - 6,
                y: padded.y - 6,
                width: padded.width + 12,
                height: padded.height + 12,
                opacity: 0,
              }}
              animate={{
                x: padded.x - 6,
                y: padded.y - 6,
                width: padded.width + 12,
                height: padded.height + 12,
                opacity: [0.6, 0.15, 0.6],
              }}
              exit={{ opacity: 0 }}
              transition={{
                ...springTransition,
                opacity: {
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                },
              }}
              rx={BORDER_RADIUS + 4}
              ry={BORDER_RADIUS + 4}
              fill="none"
              stroke={GLOW_COLOR}
              strokeWidth={2}
            />
          )}
        </AnimatePresence>
      </svg>

      {/* Pointer-event blocking: 4 strips around the cutout, or one full-screen blocker */}
      {padded ? (
        <>
          {/* Top strip */}
          <div
            className="absolute left-0 right-0 top-0"
            style={{
              height: padded.y,
              pointerEvents: 'auto',
            }}
          />
          {/* Bottom strip */}
          <div
            className="absolute left-0 right-0 bottom-0"
            style={{
              top: padded.y + padded.height,
              pointerEvents: 'auto',
            }}
          />
          {/* Left strip */}
          <div
            className="absolute left-0"
            style={{
              top: padded.y,
              width: padded.x,
              height: padded.height,
              pointerEvents: 'auto',
            }}
          />
          {/* Right strip */}
          <div
            className="absolute right-0"
            style={{
              top: padded.y,
              left: padded.x + padded.width,
              height: padded.height,
              pointerEvents: 'auto',
            }}
          />
        </>
      ) : (
        /* No cutout: block all clicks */
        <div
          className="absolute inset-0"
          style={{ pointerEvents: 'auto' }}
        />
      )}
    </div>
  );
}
